import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { ErrorFallback } from '@ygoargentina/ui/components/error-fallback';
import { BaseLoader } from '@ygoargentina/ui/components/loader';
import { useUserStore } from './store/users';
import { authService } from '@ygoargentina/sdk/api/auth-service';
import { UserRole } from '@ygoargentina/sdk/api/api.types';
import { confirmSessionLost } from './helpers/session-loss';
import Logout from './pages/logout';
import GeneralError from './pages/errors/general-error';
import MaintenanceError from './pages/errors/maintenance-error';
import NotFoundError from './pages/errors/not-found-error';
import { MaintenanceGate } from '@ygoargentina/ui/components/maintenance-gate';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Check if error is an authentication error (401)
const isAuthError = (error: Error): boolean => {
  const message = error.message?.toLowerCase() || '';

  if (
    message.includes('401') ||
    message.includes('unauthorized') ||
    message.includes('not authenticated') ||
    message.includes('authentication required') ||
    message.includes('unauthenticated')
  ) {
    return true;
  }

  const graphqlError = error as Error & { response?: { status?: number } };
  if (graphqlError.response?.status === 401) {
    return true;
  }

  return false;
};

/**
 * Reporta el error a Sentry una sola vez y devuelve el id del evento.
 *
 * Va durante el render y no en un `useEffect` porque el id se muestra en
 * pantalla: capturarlo en el efecto obligaba a un `setState` que la regla
 * `react-hooks/set-state-in-effect` marca —con razón, es un render en cascada—.
 * El `WeakMap` hace la captura idempotente por objeto `Error`, así que
 * re-renders y el doble render de StrictMode no duplican eventos.
 */
const capturedEventIds = new WeakMap<Error, string>();

const captureOnce = (error: Error): string => {
  const alreadyCaptured = capturedEventIds.get(error);
  if (alreadyCaptured) return alreadyCaptured;

  const eventId = Sentry.captureException(error);
  capturedEventIds.set(error, eventId);

  return eventId;
};

// Error boundary component that captures and reports errors to Sentry
const ErrorBoundaryWithSentry = ({ error }: { error: Error }) => {
  const isAuth = !!error && isAuthError(error);
  // Error con pinta de auth pero la sesión sigue (un permiso negado): se
  // muestra como cualquier otro error en vez de desloguear.
  const [sessionKept, setSessionKept] = useState(false);

  useEffect(() => {
    if (!isAuth) return;
    let cancelled = false;

    void confirmSessionLost('error_boundary').then((lost) => {
      if (cancelled) return;
      if (!lost) {
        setSessionKept(true);
        return;
      }
      useUserStore.getState().setUser(null);
      authService.clearCsrfToken();
      globalThis.location.reload();
    });

    return () => {
      cancelled = true;
    };
  }, [isAuth]);

  if (isAuth && !sessionKept) {
    return <BaseLoader />;
  }

  const eventId = error ? captureOnce(error) : undefined;

  // El mensaje y el id del evento se muestran también en producción: folder es
  // la app de gestión, y sin ellos un error acá es una pantalla muda — no hay
  // con qué reportarlo ni con qué encontrarlo en Sentry.
  return <ErrorFallback error={error} showDetails eventId={eventId} />;
};

// ---------------------------------------------------------------------------
// Route titles
// ---------------------------------------------------------------------------

const routeTitles = {
  '/': 'Mi Resumen',
  '/home': 'Mi Resumen',
  '/collection': 'Mi colección',
  '/boxes': 'Mi almacenamiento',
  '/expansions': 'Expansiones',
  '/decks': 'Mis mazos',
  '/cards': 'Cartas',
  '/cards/$konamiId': 'Carta',
  '/cards/$konamiId/prices': 'Carta',
  '/cards/$konamiId/metadata': 'Carta',
  '/boxes/$id': 'Caja',
  '/boxes/$id/content': 'Caja',
  '/expansions/$id': 'Expansion',
  '/expansions/$id/content': 'Expansion',
  '/expansions/$id/review': 'Expansion',
  '/expansions/$id/links': 'Expansion',
  '/expansions/$id/admin': 'Expansion',
  '/expansions/$id/languages': 'Expansion',
  '/expansions/$id/ids': 'Expansion',
  '/expansions/$id/externals': 'Expansion',
  '/leagues': 'Ligas',
  '/leagues/new': 'Nueva Liga',
  '/leagues/$id': 'Liga',
  '/leagues/$id/rules': 'Liga',
  '/leagues/$id/players': 'Liga',
  '/leagues/$id/tournaments': 'Liga',
  '/leagues/$id/ranking': 'Liga',
  '/decks/$id': 'Mazo',
  '/decks/$id/content': 'Mazo',
  '/hall-of-fame': 'Salón de la Fama',
  '/super-yacs': 'Super YACS',
  '/rulings': 'Rulings',
  '/rulings/$id': 'Ruling',
  '/settings': 'Configuración',
  '/users': 'Usuarios',
  '/users/new': 'Nuevo Usuario',
  '/users/me': 'Mi cuenta',
  '/users/me/ids': 'Mi cuenta',
  '/users/me/password': 'Mi cuenta',
  '/users/me/seller': 'Mi cuenta',
  '/users/me/keys': 'Mi cuenta',
  '/users/me/gamification': 'Mi cuenta',
  '/users/me/missions': 'Mi cuenta',
  '/users/me/leaderboard': 'Mi cuenta',
  '/users/$id': 'Usuario',
  '/users/$id/ids': 'Usuario',
  '/users/$id/keys': 'Usuario',
  '/users/$id/password': 'Usuario',
  '/users/$id/tournaments': 'Usuario',
  '/users/$id/credits': 'Creditos del Usuario',
  '/ranking': 'Ranking',
  '/ranking/historico': 'Ranking histórico',
  '/ranking/historico/$slug': 'Ranking histórico',
  '/settings/streaming': 'Streaming',
  '/organizers': 'Organizadores',
  '/organizers/new': 'Nuevo Organizador',
  '/organizers/$id': 'Organizador',
  '/organizers/$id/locations': 'Organizador',
  '/organizers/$id/images': 'Organizador',
  '/organizers/$id/employees': 'Organizador',
  '/organizers/$id/tournament-list': 'Organizador',
  '/my-organizers': 'Mis organizadores',
  '/my-store': 'Mi tienda',
  '/my-store/location': 'Tienda',
  '/my-store/timetable': 'Tienda',
  '/my-store/tournaments': 'Tienda',
  '/my-store/tournament-list': 'Tienda',
  '/my-store/employees': 'Tienda',
  '/my-store/links': 'Tienda',
  '/stores': 'Tiendas y comunidades',
  '/stores/$id': 'Tienda o Comunidad',
  '/stores/$id/location': 'Tienda o Comunidad',
  '/stores/$id/timetable': 'Tienda o Comunidad',
  '/stores/$id/tournaments': 'Tienda o Comunidad',
  '/stores/$id/tournament-list': 'Tienda o Comunidad',
  '/stores/$id/employees': 'Tienda o Comunidad',
  '/stores/$id/links': 'Tienda o Comunidad',
  '/stores/$id/images': 'Tienda o Comunidad',
  '/stores/new': 'Nueva tienda o Comunidad',
  '/my-tournaments': 'Mis torneos',
  '/players': 'Jugadores',
  '/players/$id': 'Jugador',
  '/players/$id/ids': 'Jugador',
  '/players/$id/tournaments': 'Jugador',
  '/players/$id/credits': 'Creditos del Jugador',
  '/tournaments': 'Torneos',
  '/tournaments/topdeck-pending': 'Pendientes de TopDeck',
  '/tournaments/limitless-pending': 'Pendientes de Limitless',
  '/tournaments/challonge-pending': 'Pendientes de Challonge',
  '/tournaments/kcgn': 'Torneos de KCGN',
  '/tournaments/new': 'Nuevo Torneo',
  '/tournaments/$id/import/review': 'Revision de Importacion',
  '/tournaments/$id': 'Torneo',
  '/tournaments/$id/players': 'Torneo',
  '/tournaments/$id/teams': 'Torneo',
  '/tournaments/$id/control': 'Torneo',
  '/tournaments/$id/recordings': 'Torneo',
  '/tournaments/$id/prizes': 'Torneo',
  '/tournaments/$id/links': 'Torneo',
  '/tournaments/$id/dates': 'Torneo',
  '/tournaments/$id/rounds': 'Torneo',
  '/tournaments/$id/integrations': 'Torneo',
  '/tournaments/$id/files': 'Torneo',
  '/tournaments/$id/top': 'Torneo',
  '/tournaments/$id/audit': 'Torneo',
  '/tournaments/$id/dueling-book': 'Torneo',
  '/tournaments/$id/staff': 'Torneo',
  '/content-creators': 'Creadores de contenido',
  '/content-creators/new': 'Nuevo Creador',
  '/content-creators/$id': 'Creador de Contenido',
  '/content-creators/$id/images': 'Creador de Contenido',
  '/content-creators/$id/links': 'Creador de Contenido',
  '/entrepreneurs': 'Emprendimientos',
  '/entrepreneurs/new': 'Nuevo Emprendimiento',
  '/entrepreneurs/$id': 'Emprendimiento',
  '/entrepreneurs/$id/images': 'Emprendimiento',
  '/entrepreneurs/$id/links': 'Emprendimiento',
  '/locations': 'Ubicaciones',
  '/locations/new': 'Nueva ubicación',
  '/locations/$id': 'Ubicación',
  '/locations/$id/tournaments': 'Ubicación',
  '/notifications': 'Notificaciones',
  '/banlists': 'Banlists',
  '/genesys': 'Genesys',
  '/banlists/new': 'Nueva banlist',
  '/banlists/$id': 'Banlist',
  '/banlists/$id/edit': 'Editar banlist',
  '/archetypes': 'Arquetipos y motores',
  '/archetypes/$id': 'Arquetipo',
  '/meta-decks': 'Mazos meta',
  '/sleeves': 'Fundas',
  '/playmats': 'Playmats',
  '/meta-decks/unmatched': 'Listas sin emparejar',
  '/meta-decks/$id': 'Mazo Meta',
  '/settings/cache': 'Cache',
  '/settings/formats': 'Formatos',
  '/1v1': '1 vs 1',
  '/credits': 'Créditos',
  '/orders': 'Ventas',
  '/orders/$id': 'Detalle de la Venta',
  '/my-listings': 'Mis publicaciones',
  '/my-bundles': 'Mis paquetes',
  '/wishlists': 'Buscadas',
  '/wishlists/$id': 'Lista de Buscadas',
  '/purchases': 'Compras',
  '/purchases/$id': 'Detalle de la Compra',
  '/judges': 'Jueces',
  '/settings/ranking-periods': 'Periodos de Ranking',
  '/settings/logs': 'Logs',
};

export { routeTitles };

export const getCurrentRoute = () => {
  return `${globalThis.location.hash}`;
};

export const getRouteTitle = (path: string) => {
  const routeKeys = Object.keys(routeTitles);

  const matchedKey = routeKeys.find((key) => {
    const pattern = key.replace(/\$[^/]+/g, '[^/]+');
    const regex = new RegExp(`^${pattern}$`);
    return regex.test(path);
  });

  setSubtitle('');

  return matchedKey ? routeTitles[matchedKey as keyof typeof routeTitles] : '';
};

export const setSubtitle = (subtitle: string) => {
  const subtitleElement = document.getElementById('subtitle');

  if (subtitleElement) {
    if (subtitle) {
      subtitleElement.textContent = `: ${subtitle}`;
    } else {
      subtitleElement.textContent = '';
    }
  }
};

// ---------------------------------------------------------------------------
// Root route
// ---------------------------------------------------------------------------

function RootWithMaintenance() {
  return (
    <MaintenanceGate>
      <Outlet />
    </MaintenanceGate>
  );
}

const rootRoute = createRootRoute({
  component: RootWithMaintenance,
  errorComponent: ErrorBoundaryWithSentry,
  pendingComponent: BaseLoader,
});

// ---------------------------------------------------------------------------
// App shell layout (authenticated area)
// ---------------------------------------------------------------------------

/**
 * Guarda de ruta para las pantallas de Configuración que son sólo de admin.
 *
 * El aside de `settings.tsx` ya las esconde de los demás, pero esconder el link
 * no es guardar la ruta: escribiendo la URL a mano el componente montaba, la
 * query fallaba con `unauthorized` y `isAuthError` —que matchea esa palabra—
 * terminaba deslogueando al organizador en vez de decirle que no.
 *
 * Redirige a `/settings` en vez de a `/`: ese es el único lugar que ya sabe qué
 * hacer con cada rol —manda al organizador a Escenas y al resto a la home— así
 * que la decisión vive en un solo lado.
 */
export const requireAdmin = () => {
  const user = useUserStore.getState().user;

  if (user?.roleId !== UserRole.Admin) {
    throw redirect({ to: '/settings' });
  }
};

const appShellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app-shell',
  beforeLoad: () => {
    const user = useUserStore.getState().user;
    if (!user) {
      throw redirect({ to: '/login' });
    }
  },
  component: lazyRouteComponent(() => import('./components/app-shell')),
});

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

const dashboardIndexRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/',
  component: lazyRouteComponent(() => import('./pages/dashboard/dashboard')),
});

const homeRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'home',
  component: lazyRouteComponent(() => import('./pages/dashboard/dashboard')),
});

// ---------------------------------------------------------------------------
// Auth layout (unauthenticated area)
// ---------------------------------------------------------------------------

const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'auth-layout',
  beforeLoad: () => {
    const user = useUserStore.getState().user;
    if (user) {
      throw redirect({ to: '/home' });
    }
  },
  component: lazyRouteComponent(() => import('./pages/auth/out-template')),
});

const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: 'login',
  component: lazyRouteComponent(
    () => import('./pages/auth/components/user-auth-form')
  ),
});

const loginFirstTimeRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: 'login/first-time',
  component: lazyRouteComponent(
    () => import('./pages/auth/components/first-time')
  ),
});

const registerRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: 'register',
  component: lazyRouteComponent(
    () => import('./pages/auth/components/sign-up-form')
  ),
});

const recoverRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: 'recover',
  component: lazyRouteComponent(
    () => import('./pages/auth/components/forgot-form')
  ),
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: 'reset-password',
  component: lazyRouteComponent(
    () => import('./pages/auth/components/reset-password-form')
  ),
});

// ---------------------------------------------------------------------------
// Collection
// ---------------------------------------------------------------------------

const collectionRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'collection',
  component: lazyRouteComponent(() => import('./pages/collection/collection')),
});

// ---------------------------------------------------------------------------
// Boxes
// ---------------------------------------------------------------------------

const boxesRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'boxes',
  component: lazyRouteComponent(() => import('./pages/boxes/boxes')),
});

const newBoxRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'boxes/new',
  component: lazyRouteComponent(() => import('./pages/boxes/new-box')),
});

const boxRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'boxes/$id',
  component: lazyRouteComponent(() => import('./pages/boxes/box')),
});

const boxIndexRoute = createRoute({
  getParentRoute: () => boxRoute,
  path: '/',
  component: lazyRouteComponent(
    () => import('./pages/boxes/components/box-form')
  ),
});

const boxContentRoute = createRoute({
  getParentRoute: () => boxRoute,
  path: 'content',
  component: lazyRouteComponent(
    () => import('./pages/boxes/components/box-content')
  ),
});

// ---------------------------------------------------------------------------
// Expansions
// ---------------------------------------------------------------------------

const expansionsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'expansions',
  component: lazyRouteComponent(() => import('./pages/expansions/expansions')),
});

const expansionRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'expansions/$id',
  component: lazyRouteComponent(() => import('./pages/expansions/expansion')),
});

const expansionIndexRoute = createRoute({
  getParentRoute: () => expansionRoute,
  path: '/',
  component: lazyRouteComponent(
    () => import('./pages/expansions/components/expansion-detail')
  ),
});

const expansionContentRoute = createRoute({
  getParentRoute: () => expansionRoute,
  path: 'content',
  component: lazyRouteComponent(
    () => import('./pages/expansions/components/expansion-content')
  ),
});

const expansionLinksRoute = createRoute({
  getParentRoute: () => expansionRoute,
  path: 'links',
  component: lazyRouteComponent(
    () => import('./pages/expansions/components/expansion-links')
  ),
});

const expansionIdsRoute = createRoute({
  getParentRoute: () => expansionRoute,
  path: 'ids',
  component: lazyRouteComponent(
    () => import('./pages/expansions/components/expansion-ids')
  ),
});

const expansionExternalsRoute = createRoute({
  getParentRoute: () => expansionRoute,
  path: 'externals',
  component: lazyRouteComponent(
    () => import('./pages/expansions/components/expansion-externals')
  ),
});

const expansionLanguagesRoute = createRoute({
  getParentRoute: () => expansionRoute,
  path: 'languages',
  component: lazyRouteComponent(
    () => import('./pages/expansions/components/expansion-languages')
  ),
});

const expansionAdminRoute = createRoute({
  getParentRoute: () => expansionRoute,
  path: 'admin',
  component: lazyRouteComponent(
    () => import('./pages/expansions/components/expansion-admin')
  ),
});

const expansionParentRoute = createRoute({
  getParentRoute: () => expansionRoute,
  path: 'parent',
  component: lazyRouteComponent(
    () => import('./pages/expansions/components/expansion-parent')
  ),
});

const expansionReviewRoute = createRoute({
  getParentRoute: () => expansionRoute,
  path: 'review',
  component: lazyRouteComponent(
    () => import('./pages/expansions/components/expansion-review')
  ),
});

// ---------------------------------------------------------------------------
// Streaming
// ---------------------------------------------------------------------------

const streamingRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'streaming',
  component: lazyRouteComponent(() => import('./pages/streaming/streaming')),
});

// ---------------------------------------------------------------------------
// Organizers
// ---------------------------------------------------------------------------

const organizersRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'organizers',
  component: lazyRouteComponent(() => import('./pages/organizers/organizers')),
});

const newOrganizerRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'organizers/new',
  component: lazyRouteComponent(
    () => import('./pages/organizers/new-organizer')
  ),
});

const organizerRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'organizers/$id',
  component: lazyRouteComponent(() => import('./pages/organizers/organizer')),
});

const organizerIndexRoute = createRoute({
  getParentRoute: () => organizerRoute,
  path: '/',
  component: lazyRouteComponent(
    () => import('./pages/organizers/components/organizer-detail')
  ),
});

const organizerLocationsRoute = createRoute({
  getParentRoute: () => organizerRoute,
  path: 'locations',
  component: lazyRouteComponent(
    () => import('./pages/organizers/components/organizer-locations')
  ),
});

const organizerImagesRoute = createRoute({
  getParentRoute: () => organizerRoute,
  path: 'images',
  component: lazyRouteComponent(
    () => import('./pages/organizers/components/organizer-images')
  ),
});

const organizerEmployeesRoute = createRoute({
  getParentRoute: () => organizerRoute,
  path: 'employees',
  component: lazyRouteComponent(
    () => import('./pages/organizers/components/organizer-employees-tab')
  ),
});

const organizerTournamentListRoute = createRoute({
  getParentRoute: () => organizerRoute,
  path: 'tournament-list',
  component: lazyRouteComponent(
    () => import('./pages/organizers/components/organizer-tournaments')
  ),
});

const myOrganizersRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'my-organizers',
  component: lazyRouteComponent(() => import('./pages/organizers/my-organizers')),
});

// ---------------------------------------------------------------------------
// Stores
// ---------------------------------------------------------------------------

const storesRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'stores',
  component: lazyRouteComponent(() => import('./pages/stores/stores')),
});

const newStoreRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'stores/new',
  component: lazyRouteComponent(() => import('./pages/stores/new-store')),
});

const newStoreIndexRoute = createRoute({
  getParentRoute: () => newStoreRoute,
  path: '/',
  component: lazyRouteComponent(
    () => import('./pages/stores/components/store-detail')
  ),
});

const storeRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'stores/$id',
  component: lazyRouteComponent(() => import('./pages/stores/store')),
});

const storeIndexRoute = createRoute({
  getParentRoute: () => storeRoute,
  path: '/',
  component: lazyRouteComponent(
    () => import('./pages/stores/components/store-detail')
  ),
});

const storeLocationRoute = createRoute({
  getParentRoute: () => storeRoute,
  path: 'location',
  component: lazyRouteComponent(
    () => import('./pages/stores/components/store-location')
  ),
});

const storeTimetableRoute = createRoute({
  getParentRoute: () => storeRoute,
  path: 'timetable',
  component: lazyRouteComponent(
    () => import('./pages/stores/components/store-timetable')
  ),
});

const storeTournamentsRoute = createRoute({
  getParentRoute: () => storeRoute,
  path: 'tournaments',
  component: lazyRouteComponent(
    () => import('./pages/stores/components/store-tournaments')
  ),
});

const storeTournamentListRoute = createRoute({
  getParentRoute: () => storeRoute,
  path: 'tournament-list',
  component: lazyRouteComponent(
    () => import('./pages/stores/components/store-tournament-list')
  ),
});

const storeEmployeesRoute = createRoute({
  getParentRoute: () => storeRoute,
  path: 'employees',
  component: lazyRouteComponent(
    () => import('./pages/stores/components/store-employees')
  ),
});

const storeLinksRoute = createRoute({
  getParentRoute: () => storeRoute,
  path: 'links',
  component: lazyRouteComponent(
    () => import('./pages/stores/components/store-links')
  ),
});

const storeImagesRoute = createRoute({
  getParentRoute: () => storeRoute,
  path: 'images',
  component: lazyRouteComponent(
    () => import('./pages/stores/components/store-images')
  ),
});

// ---------------------------------------------------------------------------
// My Store
// ---------------------------------------------------------------------------

const myStoreRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'my-store',
  component: lazyRouteComponent(() => import('./pages/stores/my-store')),
});

const myStoreIndexRoute = createRoute({
  getParentRoute: () => myStoreRoute,
  path: '/',
  component: lazyRouteComponent(
    () => import('./pages/stores/components/store-detail')
  ),
});

const myStoreLocationRoute = createRoute({
  getParentRoute: () => myStoreRoute,
  path: 'location',
  component: lazyRouteComponent(
    () => import('./pages/stores/components/store-location')
  ),
});

const myStoreTimetableRoute = createRoute({
  getParentRoute: () => myStoreRoute,
  path: 'timetable',
  component: lazyRouteComponent(
    () => import('./pages/stores/components/store-timetable')
  ),
});

const myStoreTournamentsRoute = createRoute({
  getParentRoute: () => myStoreRoute,
  path: 'tournaments',
  component: lazyRouteComponent(
    () => import('./pages/stores/components/store-tournaments')
  ),
});

const myStoreTournamentListRoute = createRoute({
  getParentRoute: () => myStoreRoute,
  path: 'tournament-list',
  component: lazyRouteComponent(
    () => import('./pages/stores/components/store-tournament-list')
  ),
});

const myStoreEmployeesRoute = createRoute({
  getParentRoute: () => myStoreRoute,
  path: 'employees',
  component: lazyRouteComponent(
    () => import('./pages/stores/components/store-employees')
  ),
});

const myStoreLinksRoute = createRoute({
  getParentRoute: () => myStoreRoute,
  path: 'links',
  component: lazyRouteComponent(
    () => import('./pages/stores/components/store-links')
  ),
});

// ---------------------------------------------------------------------------
// Leagues
// ---------------------------------------------------------------------------

const leaguesRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'leagues',
  component: lazyRouteComponent(() => import('./pages/leagues/leagues')),
});

const newLeagueRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'leagues/new',
  component: lazyRouteComponent(() => import('./pages/leagues/new-league')),
});

const newLeagueIndexRoute = createRoute({
  getParentRoute: () => newLeagueRoute,
  path: '/',
  component: lazyRouteComponent(
    () => import('./pages/leagues/sections'),
    'FormSection'
  ),
});

const leagueRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'leagues/$id',
  component: lazyRouteComponent(() => import('./pages/leagues/league')),
});

const leagueIndexRoute = createRoute({
  getParentRoute: () => leagueRoute,
  path: '/',
  component: lazyRouteComponent(
    () => import('./pages/leagues/sections'),
    'FormSection'
  ),
});

const leagueRulesRoute = createRoute({
  getParentRoute: () => leagueRoute,
  path: 'rules',
  component: lazyRouteComponent(
    () => import('./pages/leagues/sections'),
    'RulesSection'
  ),
});

const leaguePlayersRoute = createRoute({
  getParentRoute: () => leagueRoute,
  path: 'players',
  component: lazyRouteComponent(
    () => import('./pages/leagues/sections'),
    'PlayersSection'
  ),
});

const leagueTournamentsRoute = createRoute({
  getParentRoute: () => leagueRoute,
  path: 'tournaments',
  component: lazyRouteComponent(
    () => import('./pages/leagues/sections'),
    'TournamentsSection'
  ),
});

const leagueRankingRoute = createRoute({
  getParentRoute: () => leagueRoute,
  path: 'ranking',
  component: lazyRouteComponent(
    () => import('./pages/leagues/sections'),
    'RankingSection'
  ),
});

const leaguePaymentsRoute = createRoute({
  getParentRoute: () => leagueRoute,
  path: 'payments',
  component: lazyRouteComponent(
    () => import('./pages/leagues/sections'),
    'PaymentsSection'
  ),
});

const leagueStarsRoute = createRoute({
  getParentRoute: () => leagueRoute,
  path: 'stars',
  component: lazyRouteComponent(
    () => import('./pages/leagues/sections'),
    'StarsSection'
  ),
});

const leagueExtraPointsRoute = createRoute({
  getParentRoute: () => leagueRoute,
  path: 'extra-points',
  component: lazyRouteComponent(
    () => import('./pages/leagues/sections'),
    'ExtraPointsSection'
  ),
});

// ---------------------------------------------------------------------------
// My Tournaments
// ---------------------------------------------------------------------------

const myTournamentsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'my-tournaments',
  component: lazyRouteComponent(
    () => import('./pages/tournaments/my-tournaments')
  ),
});

// ---------------------------------------------------------------------------
// Ranking
// ---------------------------------------------------------------------------

const rankingRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'ranking',
  component: lazyRouteComponent(() => import('./pages/ranking/ranking')),
});

const rankingHistoryRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'ranking/historico',
  component: lazyRouteComponent(
    () => import('./pages/ranking/ranking-history')
  ),
});

const rankingHistorySlugRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'ranking/historico/$slug',
  component: lazyRouteComponent(
    () => import('./pages/ranking/ranking-history')
  ),
});

// ---------------------------------------------------------------------------
// Tournaments
// ---------------------------------------------------------------------------

const tournamentsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'tournaments',
  component: lazyRouteComponent(
    () => import('./pages/tournaments/tournaments')
  ),
});

const newTournamentRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'tournaments/new',
  component: lazyRouteComponent(
    () => import('./pages/tournaments/new-tournament')
  ),
});

const newTournamentIndexRoute = createRoute({
  getParentRoute: () => newTournamentRoute,
  path: '/',
  component: lazyRouteComponent(
    () => import('./pages/tournaments/components/tournament-form')
  ),
});

const tournamentImportReviewRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'tournaments/$id/import/review',
  component: lazyRouteComponent(
    () => import('./pages/tournaments/import-tournament-review')
  ),
});

const tournamentRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'tournaments/$id',
  component: lazyRouteComponent(() => import('./pages/tournaments/tournament')),
});

const tournamentIndexRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: '/',
  component: lazyRouteComponent(
    () => import('./pages/tournaments/components/tournament-form')
  ),
});

const tournamentPlayersRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: 'players',
  component: lazyRouteComponent(
    () => import('./pages/tournaments/components/tournament-players')
  ),
});

const tournamentTeamsRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: 'teams',
  component: lazyRouteComponent(
    () => import('./pages/tournaments/components/tournament-teams')
  ),
});

const tournamentControlRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: 'control',
  component: lazyRouteComponent(
    () => import('./pages/tournaments/components/tournament-control')
  ),
});

const tournamentLinksRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: 'links',
  component: lazyRouteComponent(
    () => import('./pages/tournaments/components/tournament-links')
  ),
});

const tournamentStaffRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: 'staff',
  component: lazyRouteComponent(
    () => import('./pages/tournaments/components/tournament-staff')
  ),
});

const tournamentPrizesRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: 'prizes',
  component: lazyRouteComponent(
    () => import('./pages/tournaments/components/tournament-prizes')
  ),
});

const tournamentDatesRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: 'dates',
  component: lazyRouteComponent(
    () => import('./pages/tournaments/components/tournament-dates')
  ),
});

const tournamentRoundsRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: 'rounds',
  component: lazyRouteComponent(
    () => import('./pages/tournaments/components/tournament-rounds')
  ),
});

const tournamentIntegrationsRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: 'integrations',
  component: lazyRouteComponent(
    () => import('./pages/tournaments/components/tournament-integrations')
  ),
});

const tournamentFilesRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: 'files',
  component: lazyRouteComponent(
    () => import('./pages/tournaments/components/tournament-files')
  ),
});

const tournamentTopRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: 'top',
  component: lazyRouteComponent(
    () => import('./pages/tournaments/components/tournament-tops')
  ),
});

const tournamentDuelingBookRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: 'dueling-book',
  component: lazyRouteComponent(() => import('./pages/tournaments/dueling-book')),
});

const tournamentDecklistsRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: 'decklists',
  component: lazyRouteComponent(() => import('./pages/tournaments/decklists')),
});

const tournamentRecordingsRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: 'recordings',
  component: lazyRouteComponent(() => import('./pages/tournaments/recordings')),
});

const tournamentAuditRoute = createRoute({
  getParentRoute: () => tournamentRoute,
  path: 'audit',
  component: lazyRouteComponent(() => import('./pages/tournaments/audit')),
});

const topdeckPendingRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'tournaments/topdeck-pending',
  component: lazyRouteComponent(
    () => import('./pages/tournaments/topdeck-pending')
  ),
});

const limitlessPendingRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'tournaments/limitless-pending',
  component: lazyRouteComponent(
    () => import('./pages/tournaments/limitless-pending')
  ),
});

const challongePendingRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'tournaments/challonge-pending',
  component: lazyRouteComponent(
    () => import('./pages/tournaments/challonge-pending')
  ),
});

const kcgnSnapshotsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'tournaments/kcgn',
  component: lazyRouteComponent(
    () => import('./pages/tournaments/kcgn-snapshots')
  ),
});

// ---------------------------------------------------------------------------
// Cache
// ---------------------------------------------------------------------------

const cacheRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'cache',
  beforeLoad: requireAdmin,
  component: lazyRouteComponent(() => import('./pages/cache/cache')),
});

// ---------------------------------------------------------------------------
// Hall of Fame
// ---------------------------------------------------------------------------

const hallOfFameRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'hall-of-fame',
  component: lazyRouteComponent(
    () => import('./pages/hall-of-fame/hall-of-fame')
  ),
});

const superYacsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'super-yacs',
  component: lazyRouteComponent(() => import('./pages/super-yacs/super-yacs')),
});

const rulingsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'rulings',
  component: lazyRouteComponent(() => import('./pages/rulings/rulings')),
});

const rulingEditRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'rulings/$id',
  component: lazyRouteComponent(() => import('./pages/rulings/ruling-edit')),
});

// ---------------------------------------------------------------------------
// Banlists
// ---------------------------------------------------------------------------

const banlistsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'banlists',
  component: lazyRouteComponent(() => import('./pages/banlists/banlists')),
});

const banlistRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'banlists/$id',
  component: lazyRouteComponent(() => import('./pages/banlists/banlist')),
});

const banlistNewRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'banlists/new',
  // `?base=<id>`: de cuál copiar. Es lo que manda "Duplicar" desde la tabla,
  // para llegar acá con la lista base ya elegida. Un valor que no es un id se
  // ignora en vez de romper la pantalla: viene de la URL.
  validateSearch: (search: Record<string, unknown>): { base?: number } => {
    const base = Number(search.base);

    return Number.isInteger(base) && base > 0 ? { base } : {};
  },
  component: lazyRouteComponent(
    () => import('./pages/banlists/banlist-new')
  ),
});

const banlistEditRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'banlists/$id/edit',
  component: lazyRouteComponent(
    () => import('./pages/banlists/banlist-edit')
  ),
});

// ---------------------------------------------------------------------------
// Archetypes
// ---------------------------------------------------------------------------

const archetypesRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'archetypes',
  component: lazyRouteComponent(() => import('./pages/archetypes/archetypes')),
});

const archetypeDetailRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'archetypes/$id',
  component: lazyRouteComponent(
    () => import('./pages/archetypes/archetype-detail')
  ),
});

// ---------------------------------------------------------------------------
// Meta Decks
// ---------------------------------------------------------------------------

const sleevesRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'sleeves',
  component: lazyRouteComponent(() => import('./pages/sleeves/sleeves')),
});

const playmatsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'playmats',
  component: lazyRouteComponent(() => import('./pages/playmats/playmats')),
});

const metaDecksRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'meta-decks',
  component: lazyRouteComponent(() => import('./pages/meta-decks/meta-decks')),
});

const unmatchedDecklistsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'meta-decks/unmatched',
  component: lazyRouteComponent(
    () => import('./pages/meta-decks/unmatched-decklists')
  ),
});

const metaDeckDetailRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'meta-decks/$id',
  component: lazyRouteComponent(
    () => import('./pages/meta-decks/meta-deck-detail')
  ),
});

// ---------------------------------------------------------------------------
// Content Creators
// ---------------------------------------------------------------------------

const contentCreatorsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'content-creators',
  component: lazyRouteComponent(
    () => import('./pages/content-creators/content-creators')
  ),
});

const newContentCreatorRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'content-creators/new',
  component: lazyRouteComponent(
    () => import('./pages/content-creators/new-content-creator')
  ),
});

const newContentCreatorIndexRoute = createRoute({
  getParentRoute: () => newContentCreatorRoute,
  path: '/',
  component: lazyRouteComponent(
    () => import('./pages/content-creators/components/content-creator-detail'),
    'ContentCreatorDetail'
  ),
});

const contentCreatorRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'content-creators/$id',
  component: lazyRouteComponent(
    () => import('./pages/content-creators/content-creator')
  ),
});

const contentCreatorIndexRoute = createRoute({
  getParentRoute: () => contentCreatorRoute,
  path: '/',
  component: lazyRouteComponent(
    () => import('./pages/content-creators/components/content-creator-detail'),
    'ContentCreatorDetail'
  ),
});

const contentCreatorImagesRoute = createRoute({
  getParentRoute: () => contentCreatorRoute,
  path: 'images',
  component: lazyRouteComponent(
    () => import('./pages/content-creators/components/content-creator-images'),
    'ContentCreatorImages'
  ),
});

const contentCreatorLinksRoute = createRoute({
  getParentRoute: () => contentCreatorRoute,
  path: 'links',
  component: lazyRouteComponent(
    () => import('./pages/content-creators/components/content-creator-links'),
    'ContentCreatorLinks'
  ),
});

// ---------------------------------------------------------------------------
// Entrepreneurs
// ---------------------------------------------------------------------------

const entrepreneursRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'entrepreneurs',
  component: lazyRouteComponent(
    () => import('./pages/entrepreneurs/entrepreneurs')
  ),
});

const newEntrepreneurRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'entrepreneurs/new',
  component: lazyRouteComponent(
    () => import('./pages/entrepreneurs/new-entrepreneur')
  ),
});

const newEntrepreneurIndexRoute = createRoute({
  getParentRoute: () => newEntrepreneurRoute,
  path: '/',
  component: lazyRouteComponent(
    () => import('./pages/entrepreneurs/components/entrepreneur-detail'),
    'EntrepreneurDetail'
  ),
});

const entrepreneurRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'entrepreneurs/$id',
  component: lazyRouteComponent(
    () => import('./pages/entrepreneurs/entrepreneur')
  ),
});

const entrepreneurIndexRoute = createRoute({
  getParentRoute: () => entrepreneurRoute,
  path: '/',
  component: lazyRouteComponent(
    () => import('./pages/entrepreneurs/components/entrepreneur-detail'),
    'EntrepreneurDetail'
  ),
});

const entrepreneurImagesRoute = createRoute({
  getParentRoute: () => entrepreneurRoute,
  path: 'images',
  component: lazyRouteComponent(
    () => import('./pages/entrepreneurs/components/entrepreneur-images'),
    'EntrepreneurImages'
  ),
});

const entrepreneurLinksRoute = createRoute({
  getParentRoute: () => entrepreneurRoute,
  path: 'links',
  component: lazyRouteComponent(
    () => import('./pages/entrepreneurs/components/entrepreneur-links'),
    'EntrepreneurLinks'
  ),
});

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

const settingsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'settings',
  component: lazyRouteComponent(() => import('./pages/settings/settings')),
});

const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/',
  component: lazyRouteComponent(
    () => import('./pages/settings/settings-index')
  ),
});

// ---------------------------------------------------------------------------
// Locations
// ---------------------------------------------------------------------------

const locationsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'locations',
  component: lazyRouteComponent(() => import('./pages/locations/locations')),
});

const newLocationRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'locations/new',
  component: lazyRouteComponent(() => import('./pages/locations/new-location')),
});

const newLocationIndexRoute = createRoute({
  getParentRoute: () => newLocationRoute,
  path: '/',
  component: lazyRouteComponent(
    () => import('./pages/locations/components/location-form')
  ),
});

const locationRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'locations/$id',
  component: lazyRouteComponent(() => import('./pages/locations/location')),
});

const locationIndexRoute = createRoute({
  getParentRoute: () => locationRoute,
  path: '/',
  component: lazyRouteComponent(
    () => import('./pages/locations/components/location-form')
  ),
});

const locationTournamentsRoute = createRoute({
  getParentRoute: () => locationRoute,
  path: 'tournaments',
  component: lazyRouteComponent(
    () => import('./pages/locations/components/location-tournaments')
  ),
});

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

const usersRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'users',
  component: lazyRouteComponent(() => import('./pages/users/users')),
});

const newUserRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'users/new',
  component: lazyRouteComponent(() => import('./pages/users/new-user')),
});

const userMeRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'users/me',
  component: lazyRouteComponent(() => import('./pages/users/me')),
});

const userMeIndexRoute = createRoute({
  getParentRoute: () => userMeRoute,
  path: '/',
  component: lazyRouteComponent(
    () => import('./pages/users/components/user-detail')
  ),
});

const userMeIdsRoute = createRoute({
  getParentRoute: () => userMeRoute,
  path: 'ids',
  component: lazyRouteComponent(
    () => import('./pages/users/components/user-ids')
  ),
});

const userMePasswordRoute = createRoute({
  getParentRoute: () => userMeRoute,
  path: 'password',
  component: lazyRouteComponent(
    () => import('./pages/users/components/user-password')
  ),
});

const userMeSellerRoute = createRoute({
  getParentRoute: () => userMeRoute,
  path: 'seller',
  component: lazyRouteComponent(
    () => import('./pages/users/components/user-seller-settings')
  ),
});

const userMeKeysRoute = createRoute({
  getParentRoute: () => userMeRoute,
  path: 'keys',
  component: lazyRouteComponent(
    () => import('./pages/users/components/user-keys')
  ),
});

const userMeGamificationRoute = createRoute({
  getParentRoute: () => userMeRoute,
  path: 'gamification',
  component: lazyRouteComponent(
    () => import('./pages/users/components/user-gamification')
  ),
});

const userMeMissionsRoute = createRoute({
  getParentRoute: () => userMeRoute,
  path: 'missions',
  component: lazyRouteComponent(
    () => import('./pages/users/components/user-missions')
  ),
});

const userMeLeaderboardRoute = createRoute({
  getParentRoute: () => userMeRoute,
  path: 'leaderboard',
  component: lazyRouteComponent(
    () => import('./pages/users/components/user-leaderboard')
  ),
});

const userRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'users/$id',
  component: lazyRouteComponent(() => import('./pages/users/user')),
});

const userIndexRoute = createRoute({
  getParentRoute: () => userRoute,
  path: '/',
  component: lazyRouteComponent(
    () => import('./pages/users/components/user-detail')
  ),
});

const userIdsRoute = createRoute({
  getParentRoute: () => userRoute,
  path: 'ids',
  component: lazyRouteComponent(
    () => import('./pages/users/components/user-ids')
  ),
});

const userSleeveRoute = createRoute({
  getParentRoute: () => userRoute,
  path: 'sleeve',
  component: lazyRouteComponent(
    () => import('./pages/users/components/user-sleeve')
  ),
});

const userPasswordRoute = createRoute({
  getParentRoute: () => userRoute,
  path: 'password',
  component: lazyRouteComponent(
    () => import('./pages/users/components/user-password')
  ),
});

const userKeysRoute = createRoute({
  getParentRoute: () => userRoute,
  path: 'keys',
  component: lazyRouteComponent(
    () => import('./pages/users/components/user-keys')
  ),
});

const userTournamentsRoute = createRoute({
  getParentRoute: () => userRoute,
  path: 'tournaments',
  component: lazyRouteComponent(
    () => import('./pages/users/components/user-tournaments')
  ),
});

// ---------------------------------------------------------------------------
// Players
// ---------------------------------------------------------------------------

const playersRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'players',
  component: lazyRouteComponent(() => import('./pages/users/players')),
});

const playerRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'players/$id',
  component: lazyRouteComponent(() => import('./pages/users/user')),
});

const playerIndexRoute = createRoute({
  getParentRoute: () => playerRoute,
  path: '/',
  component: lazyRouteComponent(
    () => import('./pages/users/components/user-detail')
  ),
});

const playerIdsRoute = createRoute({
  getParentRoute: () => playerRoute,
  path: 'ids',
  component: lazyRouteComponent(
    () => import('./pages/users/components/user-ids')
  ),
});

const playerTournamentsRoute = createRoute({
  getParentRoute: () => playerRoute,
  path: 'tournaments',
  component: lazyRouteComponent(
    () => import('./pages/users/components/user-tournaments')
  ),
});

const playerCreditsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'players/$id/credits',
  component: lazyRouteComponent(
    () => import('./pages/users/components/player-credits')
  ),
});

// ---------------------------------------------------------------------------
// Decks
// ---------------------------------------------------------------------------

const decksRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'decks',
  component: lazyRouteComponent(() => import('./pages/decks/decks')),
});

const deckRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'decks/$id',
  component: lazyRouteComponent(() => import('./pages/decks/deck')),
});

const deckIndexRoute = createRoute({
  getParentRoute: () => deckRoute,
  path: '/',
  component: lazyRouteComponent(
    () => import('./pages/decks/components/deck-detail')
  ),
});

const deckContentRoute = createRoute({
  getParentRoute: () => deckRoute,
  path: 'content',
  component: lazyRouteComponent(
    () => import('./pages/decks/components/deck-content')
  ),
});

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

const cardsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'cards',
  component: lazyRouteComponent(() => import('./pages/cards/cards')),
});

const cardRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'cards/$konamiId',
  component: lazyRouteComponent(() => import('./pages/cards/card')),
});

const cardIndexRoute = createRoute({
  getParentRoute: () => cardRoute,
  path: '/',
  component: lazyRouteComponent(
    () => import('./pages/cards/components/card-detail')
  ),
});

const cardPricesRoute = createRoute({
  getParentRoute: () => cardRoute,
  path: 'prices',
  component: lazyRouteComponent(
    () => import('./pages/cards/components/card-prices')
  ),
});

const cardMetadataRoute = createRoute({
  getParentRoute: () => cardRoute,
  path: 'metadata',
  beforeLoad: requireAdmin,
  component: lazyRouteComponent(
    () => import('./pages/cards/components/card-metadata')
  ),
});

// ---------------------------------------------------------------------------
// Misc pages
// ---------------------------------------------------------------------------

const oneVsOneRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '1v1',
  component: lazyRouteComponent(() => import('./pages/1vs1/1vs1')),
});

const genesysRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'genesys',
  component: lazyRouteComponent(() => import('./pages/genesys/genesys')),
});

const judgesRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'judges',
  component: lazyRouteComponent(() => import('./pages/judges/judges')),
});

const logsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'logs',
  beforeLoad: requireAdmin,
  component: lazyRouteComponent(() => import('./pages/admin/logs')),
});

const rankingPeriodsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'ranking-periods',
  beforeLoad: requireAdmin,
  component: lazyRouteComponent(() => import('./pages/admin/ranking-periods')),
});

const formatsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'formats',
  beforeLoad: requireAdmin,
  component: lazyRouteComponent(() => import('./pages/formats/formats')),
});

const regionsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'regions',
  beforeLoad: requireAdmin,
  component: lazyRouteComponent(() => import('./pages/regions/regions')),
});

const newRegionRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'regions/new',
  beforeLoad: requireAdmin,
  component: lazyRouteComponent(() => import('./pages/regions/new-region')),
});

const regionRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'regions/$id',
  beforeLoad: requireAdmin,
  component: lazyRouteComponent(() => import('./pages/regions/region')),
});

const creditsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'credits',
  component: lazyRouteComponent(() => import('./pages/users/credits')),
});

// ---------------------------------------------------------------------------
// Orders
// ---------------------------------------------------------------------------

const ordersRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'orders',
  component: lazyRouteComponent(() => import('./pages/orders/orders')),
});

const orderRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'orders/$id',
  component: lazyRouteComponent(() => import('./pages/orders/order')),
});

const myListingsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'my-listings',
  component: lazyRouteComponent(() => import('./pages/orders/my-listings')),
});

const myBundlesRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'my-bundles',
  component: lazyRouteComponent(() => import('./pages/orders/my-bundles')),
});

// ---------------------------------------------------------------------------
// Wishlists
// ---------------------------------------------------------------------------

const wishlistsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'wishlists',
  component: lazyRouteComponent(() => import('./pages/wishlists/wishlists')),
});

const wishlistRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'wishlists/$id',
  component: lazyRouteComponent(
    () => import('./pages/wishlists/components/wishlist-detail')
  ),
});

// ---------------------------------------------------------------------------
// Purchases
// ---------------------------------------------------------------------------

const purchasesRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'purchases',
  component: lazyRouteComponent(() => import('./pages/purchases/purchases')),
});

const purchaseRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: 'purchases/$id',
  component: lazyRouteComponent(() => import('./pages/purchases/purchase')),
});

// ---------------------------------------------------------------------------
// Validate Email (accessible with or without auth)
// ---------------------------------------------------------------------------

const validateEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'validate-email',
  component: lazyRouteComponent(
    () => import('./pages/auth/components/validate-email-form')
  ),
});

// ---------------------------------------------------------------------------
// Routes outside AppShell (top-level under root)
// ---------------------------------------------------------------------------

const logoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'logout',
  component: Logout,
});

const error500Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '500',
  component: GeneralError,
});

const error503Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '503',
  component: MaintenanceError,
});

const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '$',
  component: NotFoundError,
});

// ---------------------------------------------------------------------------
// Route tree
// ---------------------------------------------------------------------------

export const routeTree = rootRoute.addChildren([
  authLayoutRoute.addChildren([
    loginRoute,
    loginFirstTimeRoute,
    registerRoute,
    recoverRoute,
    resetPasswordRoute,
  ]),
  appShellRoute.addChildren([
    dashboardIndexRoute,
    homeRoute,
    collectionRoute,
    boxesRoute,
    newBoxRoute,
    boxRoute.addChildren([boxIndexRoute, boxContentRoute]),
    expansionsRoute,
    expansionRoute.addChildren([
      expansionIndexRoute,
      expansionContentRoute,
      expansionReviewRoute,
      expansionLinksRoute,
      expansionIdsRoute,
      expansionExternalsRoute,
      expansionLanguagesRoute,
      expansionAdminRoute,
      expansionParentRoute,
    ]),
    organizersRoute,
    newOrganizerRoute,
    organizerRoute.addChildren([
      organizerIndexRoute,
      organizerLocationsRoute,
      organizerImagesRoute,
      organizerEmployeesRoute,
      organizerTournamentListRoute,
    ]),
    myOrganizersRoute,
    storesRoute,
    newStoreRoute.addChildren([newStoreIndexRoute]),
    storeRoute.addChildren([
      storeIndexRoute,
      storeLocationRoute,
      storeTimetableRoute,
      storeTournamentsRoute,
      storeTournamentListRoute,
      storeEmployeesRoute,
      storeLinksRoute,
      storeImagesRoute,
    ]),
    myStoreRoute.addChildren([
      myStoreIndexRoute,
      myStoreLocationRoute,
      myStoreTimetableRoute,
      myStoreTournamentsRoute,
      myStoreTournamentListRoute,
      myStoreEmployeesRoute,
      myStoreLinksRoute,
    ]),
    leaguesRoute,
    newLeagueRoute.addChildren([newLeagueIndexRoute]),
    leagueRoute.addChildren([
      leagueIndexRoute,
      leagueRulesRoute,
      leaguePlayersRoute,
      leagueTournamentsRoute,
      leagueRankingRoute,
      leaguePaymentsRoute,
      leagueStarsRoute,
      leagueExtraPointsRoute,
    ]),
    myTournamentsRoute,
    rankingRoute,
    rankingHistoryRoute,
    rankingHistorySlugRoute,
    tournamentsRoute,
    newTournamentRoute.addChildren([newTournamentIndexRoute]),
    tournamentImportReviewRoute,
    topdeckPendingRoute,
    limitlessPendingRoute,
    challongePendingRoute,
    kcgnSnapshotsRoute,
    tournamentRoute.addChildren([
      tournamentIndexRoute,
      tournamentPlayersRoute,
      tournamentTeamsRoute,
      tournamentControlRoute,
      tournamentLinksRoute,
      tournamentStaffRoute,
      tournamentPrizesRoute,
      tournamentDatesRoute,
      tournamentRoundsRoute,
      tournamentIntegrationsRoute,
      tournamentFilesRoute,
      tournamentTopRoute,
      tournamentAuditRoute,
      tournamentDuelingBookRoute,
      tournamentDecklistsRoute,
      tournamentRecordingsRoute,
    ]),
    hallOfFameRoute,
    superYacsRoute,
    rulingsRoute,
    rulingEditRoute,
    banlistsRoute,
    banlistNewRoute,
    banlistEditRoute,
    banlistRoute,
    archetypesRoute,
    archetypeDetailRoute,
    metaDecksRoute,
    sleevesRoute,
    playmatsRoute,
    unmatchedDecklistsRoute,
    metaDeckDetailRoute,
    contentCreatorsRoute,
    newContentCreatorRoute.addChildren([newContentCreatorIndexRoute]),
    contentCreatorRoute.addChildren([
      contentCreatorIndexRoute,
      contentCreatorImagesRoute,
      contentCreatorLinksRoute,
    ]),
    entrepreneursRoute,
    newEntrepreneurRoute.addChildren([newEntrepreneurIndexRoute]),
    entrepreneurRoute.addChildren([
      entrepreneurIndexRoute,
      entrepreneurImagesRoute,
      entrepreneurLinksRoute,
    ]),
    settingsRoute.addChildren([
      settingsIndexRoute,
      cacheRoute,
      streamingRoute,
      rankingPeriodsRoute,
      logsRoute,
      formatsRoute,
      regionsRoute,
      newRegionRoute,
      regionRoute,
    ]),
    locationsRoute,
    newLocationRoute.addChildren([newLocationIndexRoute]),
    locationRoute.addChildren([locationIndexRoute, locationTournamentsRoute]),
    usersRoute,
    newUserRoute,
    userMeRoute.addChildren([
      userMeIndexRoute,
      userMeIdsRoute,
      userMeGamificationRoute,
      userMeMissionsRoute,
      userMeLeaderboardRoute,
      userMePasswordRoute,
      userMeSellerRoute,
      userMeKeysRoute,
    ]),
    userRoute.addChildren([
      userIndexRoute,
      userIdsRoute,
      userSleeveRoute,
      userPasswordRoute,
      userKeysRoute,
      userTournamentsRoute,
    ]),
    playersRoute,
    playerRoute.addChildren([
      playerIndexRoute,
      playerIdsRoute,
      playerTournamentsRoute,
    ]),
    playerCreditsRoute,
    decksRoute,
    deckRoute.addChildren([deckIndexRoute, deckContentRoute]),
    cardsRoute,
    cardRoute.addChildren([cardIndexRoute, cardPricesRoute, cardMetadataRoute]),
    oneVsOneRoute,
    genesysRoute,
    judgesRoute,
    creditsRoute,
    ordersRoute,
    orderRoute,
    myListingsRoute,
    myBundlesRoute,
    wishlistsRoute,
    wishlistRoute,
    purchasesRoute,
    purchaseRoute,
  ]),
  validateEmailRoute,
  logoutRoute,
  error500Route,
  error503Route,
  notFoundRoute,
]);

// ---------------------------------------------------------------------------
// Router factory
// ---------------------------------------------------------------------------

export function createAppRouter() {
  return createRouter({
    routeTree,
    defaultPendingComponent: BaseLoader,
    basepath: '/',
  });
}

// Type-safe router declaration
declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
